import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  {
    to: "/divisions/medical-center",
    label: "Divisions",
    children: [
      { to: "/divisions/medical-center", label: "Medical Center" },
      { to: "/divisions/medical-lab", label: "Medical Lab" },
      { to: "/divisions/pharmacy-wellness", label: "Pharmacy & Wellness" },
    ],
  },
  { to: "/team", label: "Team" },
  { to: "/why-choose-us", label: "Why IMG" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Routes that have a dark navy hero at the top (logo should be white initially).
  const darkHeroRoutes = ["/", "/about", "/contact"];
  const hasDarkHero = darkHeroRoutes.includes(location.pathname);
  const onDark = hasDarkHero && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const headerBg = onDark
    ? "bg-transparent border-b border-transparent"
    : "bg-white/95 backdrop-blur-md shadow-soft border-b border-navy-50";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${headerBg}`}
    >
      <div className="container flex h-[72px] items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center">
          <Logo variant={onDark ? "white" : "navy"} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV.map((n) => (
            <div key={n.label} className="group relative">
              <NavLink
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    isActive
                      ? onDark ? "text-teal-light" : "text-teal"
                      : onDark
                        ? "text-white/85 hover:text-teal-light"
                        : "text-navy-700 hover:text-teal"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
              {n.children && (
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="min-w-[240px] rounded-2xl bg-white shadow-card border border-navy-50 p-2">
                    {n.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`subnav-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="block px-4 py-2.5 rounded-xl text-sm font-medium text-navy-700 hover:bg-navy-50 hover:text-teal"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className={`group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
              onDark
                ? "bg-teal text-white hover:bg-teal-light hover:text-navy"
                : "bg-navy text-white hover:bg-teal"
            }`}
          >
            Request a consultation
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Open menu"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden p-2 rounded-full transition-colors ${
            onDark ? "text-white hover:bg-white/10" : "text-navy hover:bg-navy-50"
          }`}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden bg-white border-t border-navy-50 max-h-[80vh] overflow-y-auto">
          <nav className="container py-6 flex flex-col gap-1" aria-label="Mobile">
            {NAV.map((n) => (
              <div key={n.label}>
                <Link
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className="block py-3 text-base font-semibold text-navy-800"
                >
                  {n.label}
                </Link>
                {n.children && (
                  <div className="pl-3 pb-2 flex flex-col gap-1">
                    {n.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        className="py-2 text-sm text-navy-500 hover:text-teal"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-navy text-white text-sm font-semibold"
              data-testid="mobile-cta-contact"
            >
              Request a consultation
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
